<script setup lang="ts">
import AppointmentCard from '@/components/profile/AppointmentCard.vue';
import NoUpcomingAppointment from '@/components/profile/NoUpcomingAppointment.vue';
import UpcomingAppointmentCard from '@/components/profile/UpcomingAppointmentCard.vue';
import { store } from '@/store/store';
import axios from 'axios';
import { ref, watch } from 'vue';
import { usePatientFromRoute } from '@/composables/usePatientFromRoute';

interface PreviousAppointment { 
    appID: string;
    appointmentDateTime: string;
    appointmentStatus: string;
    doctorName: string;
    doctorImage: string;
    pharmacyName: string;
}

const { patient, patientId } = usePatientFromRoute();
const previousAppointments = ref<PreviousAppointment[]>([]);

watch(patientId, async (pid) => {
    if (!!pid) { 
        const response = await axios.post('/getPatientAppointmentsList', {
            pid: pid,
        });

        const pharmacies = () => {
            const isFromASingleClinic = new Set(response.data.map(x => x.pharmacyName)).size === 1;
            if (isFromASingleClinic) {
                return response.data.map(({ pharmacyName, ...rest }) => rest);
            }
            return response.data;
        }

        previousAppointments.value = pharmacies();
    }
}, { immediate: true });
</script>

<template>
    <div class="flex flex-col gap-y-10">
        <div v-if="patient && store.members.length === 1">
            <div class="text-3xl">
                Welcome <span class="title">{{ patient.name }}</span>!
            </div>
            <div v-if="patient.upcoming_appointment?.appID" class="text-xl">You have an upcoming appointment.</div>
        </div>
        <div v-if="patient && store.members.length > 1">
            <div class="text-3xl">
                <span class="title">{{ `${patient.name} ${patient.family}` }}</span>'s Profile
            </div>
            <div v-if="patient.upcoming_appointment?.appID" class="text-xl">There is an upcoming appointment:</div>
        </div>        
        <UpcomingAppointmentCard 
            v-if="patient?.upcoming_appointment?.appID" 
            :appointment="patient.upcoming_appointment" 
        />
        <NoUpcomingAppointment v-else />
        <div v-if="previousAppointments?.length">
            <div class="text-xl text-gray-800 mb-2">Previous Appointments:</div>
            <div v-for="appointment in previousAppointments" :key="appointment.appID" class="mt-3 flex flex-col gap-4">
                <AppointmentCard :appointment="appointment" />
            </div>
        </div>
    </div>
</template>
